import React, { useState } from "react";
import toast from "react-hot-toast";
import { Modal } from "./Modal";
import { InputV2 } from "../../Reusable/InputV2/InputV2";
import { Formik } from "formik";
import { GoalFormWrapper, InputSpacer } from "./Styles";
import {
  useCreateOrganizationGoalMutation,
  useEditOrganizationGoalMutation,
} from "../../../redux/reducers/apiSlice";
import { createGoalSchema } from "../../Pages/Organizations/AddNewOrganizationForm/schema";
import DiscardGoalCreationModal from "./DiscardGoalCreationModal";
import { customConsole } from "../../utils/helper";
import { Box, Text, Tooltip, Wrap, WrapItem } from "@chakra-ui/react";
import { InfoOutlineIcon } from "@chakra-ui/icons";

const GOAL_TEMPLATES = [
  {
    name: "Increase engagement",
    description:
      "Track and grow participation, event attendance, or community interaction over time.",
  },
  {
    name: "Track attendance",
    description:
      "Measure how many people show up to programs, meetings, or events.",
  },
  {
    name: "Build partnerships",
    description:
      "Set targets for new or deepened partnerships with other organizations or funders.",
  },
];

const CreateOrEditGoalModal = ({
  isOpen,
  goalId,
  goalName,
  goalDescription,
  organizationId,
  onClose,
  spark,
}) => {
  const [createGoal, { isLoading: createGoalLoading }] =
    useCreateOrganizationGoalMutation();
  const [editGoal, { isLoading: editGoalLoading }] =
    useEditOrganizationGoalMutation();
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);

  React.useEffect(() => {
    if (isOpen && !goalId) setWizardStep(0);
  }, [isOpen, goalId]);

  const isCreate = !goalId;
  const showTemplates = isCreate && wizardStep === 0;

  const loading = createGoalLoading || editGoalLoading;

  const primaryButtonText = () => {
    const loadingText = loading ? "ing..." : "";
    if (goalId) return "Edit" + loadingText;
    if (showTemplates) return "Next";
    return "Add" + loadingText;
  };

  const modalTitle = goalId
    ? "Edit goal"
    : showTemplates
    ? "Set your intention"
    : "Add details to your goal";

  return (
    <>
      <Formik
        validationSchema={createGoalSchema}
        initialValues={{
          name: goalName ?? "",
          description: goalDescription ?? "",
        }}
        onSubmit={async (goal) => {
          if (goal && spark) {
            const editMode = goalId && organizationId;
            const apiCall = editMode ? editGoal : createGoal;
            const payload = editMode
              ? {
                  organizationId,
                  goalId,
                  data: { ...goal },
                }
              : {
                  goal: { ...goal, spark },
                  organizationId,
                };
            // @ts-ignore
            await apiCall(payload).unwrap();
            onClose();
            toast.success(
              editMode
                ? `Goal "${goal.name}" updated successfully`
                : "Goal created successfully"
            );
          }
        }}
      >
        {(formik) => {
          customConsole("log", "FORMIK VALUES", formik.values);
          if (
            (formik.values.description || formik.values.name) &&
            showDiscardModal
          ) {
            return (
              <DiscardGoalCreationModal
                isOpen={showDiscardModal}
                onClose={() => setShowDiscardModal(false)}
                onPrimaryButtonClicked={onClose}
              />
            );
          }

          const handleClose = () => {
            if (showTemplates) {
              onClose();
              return;
            }
            if (formik.values.description || formik.values.name) {
              setShowDiscardModal(true);
            } else {
              onClose();
            }
          };

          const handleTemplateSelect = (name, description) => {
            formik.setValues({ name, description });
            setWizardStep(1);
          };

          return (
            <Modal
              isOpen={isOpen}
              onClose={handleClose}
              primaryButtonText={showTemplates ? undefined : primaryButtonText()}
              secondaryButtonText={showTemplates ? "Cancel" : "Cancel"}
              modalTitle={modalTitle}
              onPrimaryButtonClicked={showTemplates ? undefined : formik.handleSubmit}
              primaryLoading={loading}
              disablePrimaryButton={loading}
            >
              {showTemplates ? (
                <GoalFormWrapper>
                  <Text fontSize="sm" color="gray.600" mb={4}>
                    Choose a direction that fits, or start from scratch. You can
                    change the details on the next step.
                  </Text>
                  <Wrap spacing={3}>
                    {GOAL_TEMPLATES.map((t) => (
                      <WrapItem key={t.name}>
                        <button
                          type="button"
                          onClick={() =>
                            handleTemplateSelect(t.name, t.description)
                          }
                          style={{
                            textAlign: "left",
                            padding: "14px 18px",
                            borderRadius: "12px",
                            border: "1px solid",
                            borderColor: "var(--chakra-colors-orange-200)",
                            background: "var(--chakra-colors-orange-50)",
                            cursor: "pointer",
                            maxWidth: "260px",
                          }}
                        >
                          <Text fontWeight="600" fontSize="md" mb={1}>
                            {t.name}
                          </Text>
                          <Text fontSize="sm" color="gray.600">
                            {t.description}
                          </Text>
                        </button>
                      </WrapItem>
                    ))}
                  </Wrap>
                  <Box mt={4}>
                    <button
                      type="button"
                      onClick={() => setWizardStep(1)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "var(--chakra-colors-orange-600)",
                        fontWeight: "600",
                        fontSize: "14px",
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                    >
                      Start from scratch
                    </button>
                  </Box>
                </GoalFormWrapper>
              ) : (
                <GoalFormWrapper>
                  <InputSpacer>
                    <Box display="flex" alignItems="center" gap={2} mb={1}>
                      <Text as="label" fontSize="sm" fontWeight="500">
                        Goal name
                      </Text>
                      <Tooltip
                        label="A short, clear title for what you want to achieve (e.g. 'Increase event attendance')"
                        hasArrow
                        placement="top"
                      >
                        <span style={{ display: "inline-flex" }}>
                          <InfoOutlineIcon boxSize={3} color="gray.500" />
                        </span>
                      </Tooltip>
                    </Box>
                    <InputV2
                      placeholder="e.g. Increase engagement at our events"
                      name="name"
                    />
                  </InputSpacer>
                  <InputSpacer>
                    <Box display="flex" alignItems="center" gap={2} mb={1}>
                      <Text as="label" fontSize="sm" fontWeight="500">
                        Description
                      </Text>
                      <Tooltip
                        label="A few sentences on why this matters and how you'll know you're making progress"
                        hasArrow
                        placement="top"
                      >
                        <span style={{ display: "inline-flex" }}>
                          <InfoOutlineIcon boxSize={3} color="gray.500" />
                        </span>
                      </Tooltip>
                    </Box>
                    <InputV2
                      placeholder="Describe your goal in plain language..."
                      name="description"
                    />
                  </InputSpacer>
                </GoalFormWrapper>
              )}
            </Modal>
          );
        }}
      </Formik>
    </>
  );
};

export default CreateOrEditGoalModal;
